import { useState, useCallback, useRef } from 'react';

/**
 * Returns a [isLoading, withLoading] pair.
 * isLoading stays true for at least `minMs` milliseconds so spinners
 * are always visible long enough to be seen.
 */
export function useMinLoading(minMs = 400): [boolean, <T>(fn: () => Promise<T>) => Promise<T>] {
  const [isLoading, setIsLoading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const withLoading = useCallback(
    async <T>(fn: () => Promise<T>): Promise<T> => {
      if (timerRef.current) clearTimeout(timerRef.current);
      setIsLoading(true);
      const start = Date.now();
      try {
        return await fn();
      } finally {
        const elapsed = Date.now() - start;
        const remaining = minMs - elapsed;
        if (remaining > 0) {
          await new Promise<void>(resolve => {
            timerRef.current = setTimeout(resolve, remaining);
          });
        }
        setIsLoading(false);
      }
    },
    [minMs]
  );

  return [isLoading, withLoading];
}
